import React from 'react'
import Icons from '@olxbr/olx-icons'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { View, Link, Text, ToastMessage } from '@olxbr/design-system'

import CircularIntegerProgress from '../../organisms/CircularIntegerProgress'
import VerificationRenderer from '../../organisms/VerificationRenderer'

import Lurker from '../../../util/lurker'
import sendVerifyEmailApiCall from '../../../api/sendVerifyEmailApiCall'
import iconFail from '../../../assets/icons/alert-triangle'
import { getAccountInfo, getWalletInfo } from '../MyAccountTasksTemplate/apiCall'

const lurker = new Lurker('verification_incentive')

const Content = styled(View)`
  display: flex;
  align-items: center;
  flex-direction: column;
`

const TitleWrapper = styled(View)`
  margin-bottom: 16px;
  align-items: center;
  text-align: center;
`

const BounceLink = styled(Link)`
  margin-top: 24px;
`

const ProgressCircleWrapper = styled(View)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 16px;
  width: 96px;
  height: 96px;
`

const getStepColor = steps => {
  if (steps === 4) return '#8CE563'
  if (steps === 3) return '#FFE19A'
  if (steps === 2) return '#FFBB73'
  return '#F48787'
}

const emailSentText = (
  <Text size="caption" color="grayscale.darker">
    Reenviado
  </Text>
)

class VerificationIncentiveTemplate extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      verifications: [],
      toastMessage: '',
      toastMessageOpen: false,
      toastIcon: '',
      toastColor: '',
    }
  }

  componentDidMount() {
    lurker.pageLoadEvt()
    this.fetchData()
  }

  onPhoneClick = event => {
    const { onVerifyPhoneClick } = this.props
    lurker.clickEvt('verify_phone')
    if (event) event.preventDefault()
    onVerifyPhoneClick()
  }

  onEmailClick = async event => {
    const { verifications } = this.state
    lurker.clickEvt('verify_email')
    if (event) event.preventDefault()
    try {
      await sendVerifyEmailApiCall()
      lurker.viewMessageEvt('send_email_success')
      this.setState({
        toastMessage: 'E-mail de verificação reenviado',
        toastMessageOpen: true,
        toastIcon: '',
        toastColor: undefined,
        verifications: verifications.map(verification => {
          if (verification.title === 'Verifique seu e-mail') {
            return { ...verification, disabled: true, customExtraOptions: emailSentText }
          }
          return verification
        }),
      })
    } catch (error) {
      const {
        response: { status, data: { message = 'unexpected_error' } = {} } = {},
        message: exceptionMessage = 'unknown',
      } = error
      lurker.viewMessageEvt('send_email_error', {
        accounts_reason: message.toLowerCase(),
        accounts_status_code: status,
        accounts_error_message: exceptionMessage,
      })
      this.setState({
        toastMessage: 'Não foi possível reenviar o e-mail de verificação',
        toastMessageOpen: true,
        toastIcon: iconFail,
        toastColor: '#FF4444',
      })
    }
  }

  onWalletClick = event => {
    const { onActivateWalletClick } = this.props
    lurker.clickEvt('activate_wallet')
    if (event) event.preventDefault()
    onActivateWalletClick()
  }

  getAccountVerifications = async () => {
    let emailVerified = false
    let phoneVerified = false
    try {
      const { data } = await getAccountInfo()
      emailVerified = !!data?.emailVerified
      phoneVerified = !!data?.phoneVerified
    } catch (error) {
      // Do nothing
    }

    return [
      {
        title: emailVerified ? 'E-mail verificado' : 'Verifique seu e-mail',
        subtitle: 'Receba as comunicações da conta e alertas de segurança.',
        Icon: Icons.System.Mail,
        verified: emailVerified,
        onClick: this.onEmailClick,
      },
      {
        title: phoneVerified ? 'Telefone verificado' : 'Verifique seu telefone',
        subtitle: 'Conta segura para anunciar e interagir com outros usuários.',
        Icon: Icons.System.Phone,
        verified: phoneVerified,
        onClick: this.onPhoneClick,
      },
    ]
  }

  getWalletVerifications = async () => {
    let hasWallet = false
    try {
      const {
        data: { status },
      } = await getWalletInfo()
      hasWallet = status === 'ACTIVE'
    } catch (error) {
      // Do nothing
    }

    return [
      {
        title: hasWallet ? 'Carteira OLX ativada' : 'Ative a Carteira OLX',
        subtitle: 'Compre e venda com mais segurança e praticidade.',
        Icon: Icons.System.Wallet,
        verified: hasWallet,
        onClick: this.onWalletClick,
      },
    ]
  }

  bounceClick = event => {
    const { onBounceClick } = this.props
    lurker.clickEvt('bounce')
    if (event) event.preventDefault()
    onBounceClick()
  }

  async fetchData() {
    const { onVerificationCompleted } = this.props
    const walletVerification = await this.getWalletVerifications()
    const accountVerifications = await this.getAccountVerifications()
    const verifications = [...accountVerifications, ...walletVerification]
    this.setState({ verifications }, () => {
      onVerificationCompleted(verifications.every(({ verified }) => verified))
    })
  }

  render() {
    const { verifications, toastMessage, toastMessageOpen, toastIcon, toastColor } = this.state
    const steps = (verifications.length || 3) + 1
    const stepsCompleted = verifications.filter(({ verified }) => verified).length + 1 || 0
    const stepColor = getStepColor(stepsCompleted)
    return (
      <Content>
        <ProgressCircleWrapper>
          <CircularIntegerProgress
            strokeWidth={6}
            steps={steps}
            stepsCompleted={stepsCompleted}
            styles={{
              path: {
                strokeLinecap: 'butt',
                stroke: stepColor,
              },
              text: {
                fontFamily: 'Nunito Sans',
                fill: '#999',
                fontSize: 18,
                fontWeight: 600,
                lineHeight: 16,
                textAlign: 'center',
              },
            }}
          >
            <Icons.System.Award color={stepColor} size="24" />
          </CircularIntegerProgress>
        </ProgressCircleWrapper>
        <TitleWrapper>
          <Text size="title3" weight="semiBold">
            Vamos aumentar o nível de segurança da sua conta?
          </Text>
        </TitleWrapper>
        <VerificationRenderer verifications={verifications} />
        <BounceLink onClick={this.bounceClick} fontWeight={600} type="primary" underline={false}>
          Fazer isso depois
        </BounceLink>
        <ToastMessage
          text={toastMessage}
          show={toastMessageOpen}
          iconPath={toastIcon}
          iconColor={toastColor}
          onClose={() => this.setState({ toastMessageOpen: false })}
        />
      </Content>
    )
  }
}

VerificationIncentiveTemplate.propTypes = {
  onActivateWalletClick: PropTypes.func.isRequired,
  onBounceClick: PropTypes.func.isRequired,
  onVerificationCompleted: PropTypes.func.isRequired,
  onVerifyPhoneClick: PropTypes.func.isRequired,
}

export default VerificationIncentiveTemplate
